import React, { useState, useEffect, useCallback, useRef } from 'react';
import OptimizedImage from '../OptimizedImage/OptimizedImage';
import './GallerySection.css';

const GALLERY_IMAGES = [
  { id: 1, src: '/Website-Images/Gallery/A1.webp', alt: 'ASW Cars Delivery 1' },
  { id: 2, src: '/Website-Images/Gallery/A2.webp', alt: 'ASW Cars Delivery 2' },
  { id: 3, src: '/Website-Images/Gallery/A3.webp', alt: 'ASW Cars Delivery 3' },
  { id: 4, src: '/Website-Images/Gallery/A4.webp', alt: 'ASW Cars Delivery 4' },
  { id: 5, src: '/Website-Images/Gallery/A5.webp', alt: 'ASW Cars Delivery 5' },
  { id: 6, src: '/Website-Images/Gallery/A6.webp', alt: 'ASW Cars Delivery 6' },
  { id: 7, src: '/Website-Images/Gallery/A7.webp', alt: 'ASW Cars Delivery 7' },
  { id: 8, src: '/Website-Images/Gallery/A8.webp', alt: 'ASW Cars Delivery 8' },
  { id: 9, src: '/Website-Images/Gallery/A9.webp', alt: 'ASW Cars Delivery 9' },
  { id: 10, src: '/Website-Images/Gallery/A10.webp', alt: 'ASW Cars Delivery 10' },
];

const GallerySection: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [deviceType, setDeviceType] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [isPaused, setIsPaused] = useState(false);
  
  // Lightbox modal state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Touch gesture state
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const carouselRef = useRef<HTMLDivElement>(null);

  // Responsive items count
  const getImagesPerView = useCallback(() => {
    if (deviceType === 'mobile') return 1;
    if (deviceType === 'tablet') return 2;
    return 3; // desktop
  }, [deviceType]);

  // Update device breakpoint
  const updateDeviceType = useCallback(() => {
    const width = window.innerWidth;
    if (width <= 640) {
      setDeviceType('mobile');
    } else if (width <= 1024) {
      setDeviceType('tablet');
    } else {
      setDeviceType('desktop');
    }
  }, []);

  useEffect(() => {
    updateDeviceType();
    window.addEventListener('resize', updateDeviceType);
    return () => window.removeEventListener('resize', updateDeviceType);
  }, [updateDeviceType]);

  const imagesPerView = getImagesPerView();
  const maxIndex = Math.max(0, GALLERY_IMAGES.length - imagesPerView);

  // Auto-play carousel unless paused or lightbox open
  useEffect(() => {
    if (isPaused || lightboxOpen || maxIndex <= 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 3800);

    return () => clearInterval(interval);
  }, [isPaused, lightboxOpen, maxIndex]);

  // Manual slide transition
  const goToSlide = (index: number) => {
    setCurrentIndex(Math.max(0, Math.min(index, maxIndex)));
  };

  const handlePrev = () => {
    goToSlide(currentIndex > 0 ? currentIndex - 1 : maxIndex);
  };

  const handleNext = () => {
    goToSlide(currentIndex < maxIndex ? currentIndex + 1 : 0);
  };

  // Touch Swipe Handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) {
      handleNext();
    } else if (distance < -50) {
      handlePrev();
    }
    setTouchStart(0);
    setTouchEnd(0);
  };

  // Lightbox handlers
  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextLightboxImage = () => {
    setLightboxIndex((prev) => (prev + 1) % GALLERY_IMAGES.length);
  };

  const prevLightboxImage = () => {
    setLightboxIndex((prev) => (prev - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextLightboxImage();
      if (e.key === 'ArrowLeft') prevLightboxImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  return (
    <section className="asw-gallery-section" id="gallery">
      <div className="asw-gallery-container">
        
        {/* ── Section Header ── */}
        <div className="asw-gallery-header">
          <div className="asw-gallery-badge">
            <span className="badge-pulse-dot"></span>
            <span>Customer Celebrations & Deliveries</span>
          </div>

          <h2 className="asw-gallery-title">
            Our <span className="title-highlight">Gallery</span>
          </h2>

          <p className="asw-gallery-subtitle">
            Real celebrations of our happy vehicle owners across Maharashtra.
          </p>
        </div>

        {/* ── Carousel Slider ── */}
        <div 
          className="asw-gallery-carousel-wrap"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Previous Arrow */}
          <button 
            type="button"
            className="asw-gallery-arrow prev" 
            onClick={handlePrev}
            aria-label="Previous Slide"
            title="Previous"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          {/* Carousel Track Container */}
          <div 
            className="asw-gallery-track-container" 
            ref={carouselRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              className="asw-gallery-track"
              style={{
                transform: `translateX(-${currentIndex * (100 / imagesPerView)}%)`,
                transition: 'transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1)'
              }}
            >
              {GALLERY_IMAGES.map((item, index) => (
                <div 
                  key={item.id}
                  className="asw-gallery-card-item"
                  style={{ flex: `0 0 ${100 / imagesPerView}%` }}
                >
                  <div 
                    className="asw-gallery-card" 
                    onClick={() => openLightbox(index)}
                    title="Click to view full photo"
                  >
                    {/* Clean Pure Image Box */}
                    <div className="card-image-box">
                      <OptimizedImage
                        src={item.src}
                        alt={item.alt}
                        loading={index < 4 ? 'eager' : 'lazy'}
                        className="asw-gallery-img"
                      />
                      
                      {/* Subtle hover icon */}
                      <div className="card-zoom-overlay">
                        <span className="zoom-icon-badge">
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            <line x1="11" y1="8" x2="11" y2="14"></line>
                            <line x1="8" y1="11" x2="14" y2="11"></line>
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Next Arrow */}
          <button 
            type="button"
            className="asw-gallery-arrow next" 
            onClick={handleNext}
            aria-label="Next Slide"
            title="Next"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>

        {/* ── Carousel Pagination Dots ── */}
        {maxIndex > 0 && (
          <div className="asw-gallery-dots">
            {Array.from({ length: maxIndex + 1 }).map((_, dotIdx) => (
              <button
                key={dotIdx}
                type="button"
                className={`gallery-bullet-dot ${currentIndex === dotIdx ? 'active' : ''}`}
                onClick={() => goToSlide(dotIdx)}
                aria-label={`Go to slide ${dotIdx + 1}`}
              />
            ))}
          </div>
        )}

        {/* ── Bottom Trust Callout Strip ── */}
        <div className="asw-gallery-cta-banner">
          <div className="cta-banner-content">
            <div className="cta-icon-box">
              <i className="fa-solid fa-award"></i>
            </div>
            <div className="cta-text-box">
              <h4 className="cta-banner-heading">Want Your Own Celebration Delivery Photo?</h4>
              <p className="cta-banner-sub">
                Get up to 100% on-road funding, lowest interest rates, and transparent vehicle handovers within 24–48 hours.
              </p>
            </div>
          </div>

          <div className="cta-banner-actions">
            <a 
              href="https://wa.me/919987828417?text=Hi%20ASW%20Cars,%20I%20saw%20your%20Customer%20Gallery%20and%20want%20to%20know%20about%20Car%20Deals." 
              target="_blank" 
              rel="noopener noreferrer" 
              className="cta-btn-whatsapp"
            >
              <i className="fa-brands fa-whatsapp"></i>
              <span>Book Your Car on WhatsApp</span>
            </a>
            <a href="tel:+919987828690" className="cta-btn-call">
              <i className="fa-solid fa-phone"></i>
              <span>Call +91 99878 28690</span>
            </a>
          </div>
        </div>

      </div>

      {/* ── Fullscreen Clean Lightbox Modal ── */}
      {lightboxOpen && GALLERY_IMAGES[lightboxIndex] && (
        <div className="asw-lightbox-backdrop" onClick={closeLightbox}>
          <div className="asw-lightbox-modal" onClick={(e) => e.stopPropagation()}>
            
            {/* Top Toolbar */}
            <div className="asw-lightbox-topbar">
              <span className="lightbox-count">
                {lightboxIndex + 1} / {GALLERY_IMAGES.length}
              </span>
              <button 
                type="button" 
                className="lightbox-close-btn" 
                onClick={closeLightbox}
                title="Close (Esc)"
              >
                ✕
              </button>
            </div>

            {/* Main Stage */}
            <div className="asw-lightbox-stage">
              <button 
                type="button" 
                className="lightbox-nav-btn prev" 
                onClick={prevLightboxImage}
                title="Previous Photo"
              >
                ‹
              </button>

              <div className="lightbox-img-wrapper">
                <img 
                  src={GALLERY_IMAGES[lightboxIndex].src} 
                  alt={GALLERY_IMAGES[lightboxIndex].alt}
                  className="lightbox-main-img" 
                />
              </div>

              <button 
                type="button" 
                className="lightbox-nav-btn next" 
                onClick={nextLightboxImage}
                title="Next Photo"
              >
                ›
              </button>
            </div>

            {/* Clean Bottom Action */}
            <div className="asw-lightbox-bottom">
              <div className="lightbox-action-row">
                <a 
                  href="https://wa.me/919987828417?text=Hi%20ASW%20Cars,%20I%20am%20interested%20in%20a%20car%20similar%20to%20your%20delivery%20photo." 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="lightbox-enquiry-btn"
                >
                  <i className="fa-brands fa-whatsapp"></i>
                  <span>Enquire on WhatsApp</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      )}
    </section>
  );
};

export default GallerySection;